import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useCart } from '../context/CartContext';
import axios from 'axios';
import { toast } from '../hooks/use-toast';
import { Toaster } from '../components/ui/toaster';
import { CreditCard, Shield, Lock, CheckCircle, AlertCircle, Loader2, Tag } from 'lucide-react';
import { resolveImageUrl } from '../utils/imageUrl';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const RAZORPAY_KEY_ID = process.env.REACT_APP_RAZORPAY_KEY_ID;

const Payment = () => {
  const { cart, getCartTotal, clearCart } = useCart();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [contactInfo, setContactInfo] = useState(null);
  const [shippingInfo, setShippingInfo] = useState({ shipping_charge: 0, rule_name: '', rule_id: '', is_free: false });
  const [appliedPromo, setAppliedPromo] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState(null); // 'success', 'failed', null
  const [orderId, setOrderId] = useState(null);
  const [isBuyNowFlow, setIsBuyNowFlow] = useState(false);
  const [buyNowItem, setBuyNowItem] = useState(null);

  useEffect(() => {
    // Get contact info and shipping info from sessionStorage
    const storedContactInfo = sessionStorage.getItem('checkoutContactInfo');
    const storedShippingInfo = sessionStorage.getItem('shippingInfo');
    const storedIsBuyNowFlow = sessionStorage.getItem('isBuyNowFlow');
    const storedBuyNowItem = sessionStorage.getItem('buyNowItem');
    const storedPromo = sessionStorage.getItem('appliedPromo');
    
    const buyNowFlowActive = storedIsBuyNowFlow ? JSON.parse(storedIsBuyNowFlow) : false;
    setIsBuyNowFlow(buyNowFlowActive);
    
    if (storedBuyNowItem) {
      setBuyNowItem(JSON.parse(storedBuyNowItem));
    }
    
    // Redirect if no contact info or no items
    if (!storedContactInfo || (cart.length === 0 && !buyNowFlowActive)) {
      navigate('/contact-info');
      return;
    }
    setContactInfo(JSON.parse(storedContactInfo));
    
    if (storedShippingInfo) {
      setShippingInfo(JSON.parse(storedShippingInfo));
    }
    if (storedPromo) {
      try { setAppliedPromo(JSON.parse(storedPromo)); } catch (e) { /* ignore */ }
    }
  }, [cart, navigate]);

  // Get display items based on flow type
  const displayItems = isBuyNowFlow && buyNowItem 
    ? [{ ...buyNowItem, id: buyNowItem._id || buyNowItem.id }] 
    : cart;
  
  // Calculate subtotal based on flow type
  const getSubtotal = () => {
    if (isBuyNowFlow && buyNowItem) {
      return buyNowItem.price * buyNowItem.quantity;
    }
    return getCartTotal();
  };

  // Load Razorpay script
  const loadRazorpayScript = useCallback(() => {
    return new Promise((resolve) => {
      if (window.Razorpay) {
        resolve(true);
        return;
      }
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  }, []);

  const handlePayment = async () => {
    if (!contactInfo) {
      toast({
        title: "Contact info missing",
        description: "Please fill in your contact information first.",
        variant: "destructive"
      });
      navigate('/contact-info');
      return;
    }

    setLoading(true);

    try {
      // Load Razorpay script
      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        throw new Error('Failed to load Razorpay. Please check your internet connection.');
      }

      const subtotal = getSubtotal();
      const shipping = shippingInfo.shipping_charge || 0;
      const discount = appliedPromo?.discount_amount || 0;
      const total = Math.max(0, subtotal - discount) + shipping;
      const amountInPaise = Math.round(total * 100);

      // Prepare items for order based on flow type
      const orderItems = isBuyNowFlow && buyNowItem
        ? [{
            product_id: buyNowItem._id || buyNowItem.id,
            quantity: buyNowItem.quantity,
            price: buyNowItem.price
          }]
        : cart.map(item => ({
            product_id: item.id,
            quantity: item.quantity,
            price: item.price
          }));

      // Create order on backend with shipping info (backend re-validates promo and recomputes total)
      const orderResponse = await axios.post(`${API}/payment/create-order`, {
        amount: amountInPaise,
        customer_name: contactInfo.customer_name,
        customer_email: contactInfo.customer_email,
        customer_phone: contactInfo.customer_phone,
        customer_address: contactInfo.customer_address,
        pincode: contactInfo.pincode,
        items: orderItems,
        subtotal: subtotal,
        shipping_charge: shipping,
        shipping_rule_name: shippingInfo.rule_name || '',
        shipping_rule_id: shippingInfo.rule_id || '',
        promo_code: appliedPromo?.code || ''
      });

      const { razorpay_order_id, key_id, order_db_id } = orderResponse.data;

      // Razorpay checkout options
      const options = {
        key: key_id || RAZORPAY_KEY_ID,
        amount: amountInPaise,
        currency: 'INR',
        name: 'Jain Watch Company',
        description: 'Watch Purchase',
        order_id: razorpay_order_id,
        prefill: {
          name: contactInfo.customer_name,
          email: contactInfo.customer_email,
          contact: contactInfo.customer_phone
        },
        notes: {
          address: contactInfo.customer_address,
          pincode: contactInfo.pincode
        },
        theme: {
          color: '#EAB308' // Yellow-500
        },
        handler: async function(response) {
          // Payment successful - verify on backend
          try {
            setLoading(true);
            const verifyResponse = await axios.post(`${API}/payment/verify`, {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              order_db_id: order_db_id
            });

            if (verifyResponse.data.success) {
              setPaymentStatus('success');
              setOrderId(verifyResponse.data.order_id);
              
              // Clear cart only if not in buy now flow
              if (!isBuyNowFlow) {
                clearCart();
              }
              
              // Clean up all session storage
              sessionStorage.removeItem('checkoutContactInfo');
              sessionStorage.removeItem('shippingInfo');
              sessionStorage.removeItem('isBuyNowFlow');
              sessionStorage.removeItem('buyNowItem');
              sessionStorage.removeItem('appliedPromo');
              
              toast({
                title: "Payment Successful!",
                description: "Your order has been placed. Check your email for confirmation.",
              });
            }
          } catch (error) {
            console.error('Payment verification failed:', error);
            setPaymentStatus('failed');
            toast({
              title: "Payment verification failed",
              description: "Please contact support with your payment details.",
              variant: "destructive"
            });
          } finally {
            setLoading(false);
          }
        },
        modal: {
          ondismiss: async function() {
            // RACE-CONDITION FIX: Don't immediately mark as failed.
            // Razorpay's handler() (which calls /payment/verify) may still be in-flight
            // when the modal dismisses (especially on UPI flow on mobile).
            // Wait briefly, then check the order's actual status on backend.
            setLoading(true);
            
            const checkStatusAndDecide = async () => {
              try {
                const statusResp = await axios.get(`${API}/orders/${order_db_id}/status`);
                const orderStatus = statusResp.data?.status;
                
                if (orderStatus === 'confirmed' || statusResp.data?.payment_verified) {
                  // Payment actually succeeded; verify ran. Show success page.
                  setPaymentStatus('success');
                  setOrderId(order_db_id);
                  if (!isBuyNowFlow) clearCart();
                  sessionStorage.removeItem('checkoutContactInfo');
                  sessionStorage.removeItem('shippingInfo');
                  sessionStorage.removeItem('isBuyNowFlow');
                  sessionStorage.removeItem('buyNowItem');
                  sessionStorage.removeItem('appliedPromo');
                  toast({
                    title: "Payment Successful!",
                    description: "Your order has been placed. Check your email for confirmation.",
                  });
                  return;
                }
                
                // Truly dismissed without payment — mark failed
                await axios.post(`${API}/payment/failed?order_db_id=${order_db_id}`);
                setPaymentStatus('failed');
                toast({
                  title: "Payment cancelled",
                  description: "You closed the payment window. You can try again.",
                  variant: "destructive"
                });
              } catch (e) {
                console.error('Status check failed:', e);
                setPaymentStatus('failed');
              } finally {
                setLoading(false);
              }
            };
            
            // Give verify ~3s grace period to complete before deciding
            setTimeout(checkStatusAndDecide, 3000);
          }
        }
      };

      const razorpay = new window.Razorpay(options);
      
      razorpay.on('payment.failed', async function(response) {
        console.error('Payment failed:', response.error);
        setPaymentStatus('failed');
        setLoading(false);
        
        // Update order status on backend
        try {
          await axios.post(`${API}/payment/failed?order_db_id=${order_db_id}`);
        } catch (e) {
          console.error('Failed to update order status:', e);
        }
        
        toast({
          title: "Payment Failed",
          description: response.error.description || "Please try again.",
          variant: "destructive"
        });
      });

      razorpay.open();
      
    } catch (error) {
      console.error('Error initiating payment:', error);
      toast({
        title: "Payment Error",
        description: error.response?.data?.detail || error.message || "Failed to initiate payment. Please try again.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  // Show success page with "Thank you for your payment" and auto-redirect
  if (paymentStatus === 'success') {
    // Auto redirect to home page after 4 seconds
    setTimeout(() => {
      navigate('/');
    }, 4000);

    return (
      <div className="min-h-screen bg-gray-50" data-testid="payment-success-page">
        <Header />
        <div className="container mx-auto px-4 py-16 text-center">
          <div className="max-w-md mx-auto bg-white rounded-lg shadow-lg p-8">
            <CheckCircle className="w-20 h-20 text-green-500 mx-auto mb-6" />
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4" data-testid="thank-you-message">
              Thank you for your payment
            </h1>
            <p className="text-gray-600 mb-2">Your order has been successfully placed.</p>
            <p className="text-gray-600 mb-6">
              Order ID: <span className="font-mono font-semibold">{orderId}</span>
            </p>
            <p className="text-sm text-gray-500 mb-4">
              A confirmation email has been sent to your email address.
            </p>
            <p className="text-sm text-yellow-600 font-medium mb-6">
              Redirecting to home page in a few seconds...
            </p>
            <button
              onClick={() => navigate('/')}
              data-testid="continue-shopping-btn"
              className="w-full bg-yellow-500 hover:bg-yellow-600 text-black font-bold py-3 px-6 rounded-lg transition-colors"
            >
              Go to Home Page
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // Show failed page - keep user on payment page with retry option
  if (paymentStatus === 'failed') {
    return (
      <div className="min-h-screen bg-gray-50" data-testid="payment-failed-page">
        <Header />
        <div className="container mx-auto px-4 py-16 text-center">
          <div className="max-w-md mx-auto bg-white rounded-lg shadow-lg p-8 border-2 border-red-200">
            <AlertCircle className="w-20 h-20 text-red-500 mx-auto mb-6" />
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4" data-testid="payment-failed-message">
              Payment Failed
            </h1>
            <p className="text-gray-600 mb-6">
              Your payment could not be processed. No amount has been deducted from your account.
            </p>
            <p className="text-sm text-gray-500 mb-6">
              Please try again or use a different payment method.
            </p>
            <div className="space-y-3">
              <button
                onClick={() => {
                  setPaymentStatus(null);
                  handlePayment();
                }}
                data-testid="retry-payment-btn"
                className="w-full bg-yellow-500 hover:bg-yellow-600 text-black font-bold py-3 px-6 rounded-lg transition-colors"
              >
                Try Again
              </button>
              <button
                onClick={() => {
                  setPaymentStatus(null);
                }}
                data-testid="change-payment-btn"
                className="w-full bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold py-3 px-6 rounded-lg transition-colors"
              >
                Back to Payment Options
              </button>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (!contactInfo || (cart.length === 0 && !isBuyNowFlow)) {
    return null;
  }

  const subtotal = getSubtotal();
  const shipping = shippingInfo.shipping_charge || 0;
  const discount = appliedPromo?.discount_amount || 0;
  const total = Math.max(0, subtotal - discount) + shipping;

  return (
    <div className="min-h-screen bg-gray-50" data-testid="payment-page">
      <Header />
      <Toaster />

      <div className="bg-black text-white py-6 sm:py-8 md:py-12">
        <div className="container mx-auto px-4">
          <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold">Payment</h1>
          <div className="flex items-center mt-4 text-sm">
            <span className="text-gray-400">1. Contact Info</span>
            <div className="w-8 h-0.5 bg-yellow-500 mx-2"></div>
            <span className="bg-yellow-500 text-black px-3 py-1 rounded-full font-semibold">2. Payment</span>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-3 sm:px-4 py-4 sm:py-6 md:py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
          {/* Payment Section */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow-md p-4 sm:p-6" data-testid="payment-section">
              <h2 className="text-lg sm:text-xl md:text-2xl font-bold mb-4 sm:mb-6 flex items-center">
                <CreditCard className="w-5 h-5 sm:w-6 sm:h-6 mr-2 text-yellow-500" />
                Payment Method
              </h2>

              {/* Delivery Info Summary */}
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <h3 className="font-semibold text-gray-700 mb-3">Delivering to:</h3>
                <div className="text-sm text-gray-600 space-y-1">
                  <p><span className="font-medium">Name:</span> {contactInfo.customer_name}</p>
                  <p><span className="font-medium">Phone:</span> {contactInfo.customer_phone}</p>
                  <p><span className="font-medium">Email:</span> {contactInfo.customer_email}</p>
                  <p><span className="font-medium">Address:</span> {contactInfo.customer_address}</p>
                  <p><span className="font-medium">Pincode:</span> {contactInfo.pincode}</p>
                </div>
                <button
                  onClick={() => navigate('/checkout')}
                  className="text-yellow-600 hover:text-yellow-700 text-sm font-semibold mt-3"
                >
                  Edit Details
                </button>
              </div>

              {/* Razorpay Payment */}
              <div className="border-2 border-yellow-500 rounded-lg p-4 sm:p-6 bg-yellow-50">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center shadow-sm">
                      <img 
                        src="https://razorpay.com/favicon.png" 
                        alt="Razorpay" 
                        className="w-8 h-8"
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    </div>
                    <div>
                      <p className="font-bold text-gray-900">Pay with Razorpay</p>
                      <p className="text-xs sm:text-sm text-gray-600">UPI, Cards, Net Banking, Wallets</p>
                    </div>
                  </div>
                  <Shield className="w-8 h-8 text-green-600" />
                </div>

                <div className="grid grid-cols-4 gap-2 mb-4">
                  <div className="bg-white rounded p-2 text-center text-xs font-medium text-gray-600">UPI</div>
                  <div className="bg-white rounded p-2 text-center text-xs font-medium text-gray-600">Cards</div>
                  <div className="bg-white rounded p-2 text-center text-xs font-medium text-gray-600">Net Banking</div>
                  <div className="bg-white rounded p-2 text-center text-xs font-medium text-gray-600">Wallets</div>
                </div>

                <button
                  onClick={handlePayment}
                  disabled={loading}
                  data-testid="pay-now-btn"
                  className="w-full bg-yellow-500 hover:bg-yellow-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-black font-bold py-4 rounded-lg transition-all duration-300 transform hover:scale-105 flex items-center justify-center space-x-2"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      <span>Processing...</span>
                    </>
                  ) : (
                    <>
                      <Lock className="w-5 h-5" />
                      <span>Pay Now ₹{total.toLocaleString()}</span>
                    </>
                  )}
                </button>
              </div>

              {/* Security Info */}
              <div className="mt-6 flex items-start space-x-3 text-sm text-gray-600">
                <Lock className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <p>
                  Your payment is secured with 256-bit SSL encryption. We do not store your card details.
                </p>
              </div>
            </div>
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-md p-4 sm:p-6 sticky top-20 sm:top-24" data-testid="order-summary">
              <h2 className="text-lg sm:text-xl md:text-2xl font-bold mb-4 sm:mb-6">Order Summary</h2>
              
              <div className="space-y-3 sm:space-y-4 mb-4 sm:mb-6 max-h-64 sm:max-h-96 overflow-y-auto">
                {displayItems.map((item) => (
                  <div key={item.id} className="flex gap-2 sm:gap-4 pb-3 sm:pb-4 border-b">
                    <img
                      src={resolveImageUrl(item.image)}
                      alt={item.name}
                      className="w-12 h-12 sm:w-16 sm:h-16 object-cover rounded"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-xs sm:text-sm line-clamp-2">{item.name}</p>
                      <p className="text-xs text-gray-600">Qty: {item.quantity}</p>
                      <p className="text-xs sm:text-sm font-bold">₹{(item.price * item.quantity).toLocaleString()}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="space-y-2 sm:space-y-3 mb-4 sm:mb-6">
                <div className="flex justify-between text-sm sm:text-base">
                  <span className="text-gray-600">Subtotal</span>
                  <span className="font-semibold">₹{subtotal.toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-sm sm:text-base">
                  <span className="text-gray-600">Shipping</span>
                  <span className="font-semibold">
                    {shipping === 0 || shippingInfo.is_free ? <span className="text-green-600">FREE</span> : `₹${shipping}`}
                  </span>
                </div>
                {shippingInfo.rule_name && (
                  <p className="text-xs text-gray-500">{shippingInfo.rule_name}</p>
                )}
                {appliedPromo && (
                  <div className="flex justify-between text-sm sm:text-base" data-testid="payment-summary-discount">
                    <span className="text-green-700 flex items-center gap-1">
                      <Tag className="w-3.5 h-3.5" />
                      Promo ({appliedPromo.code})
                    </span>
                    <span className="font-semibold text-green-700">
                      − ₹{Math.round(appliedPromo.discount_amount).toLocaleString()}
                    </span>
                  </div>
                )}
                <div className="border-t pt-2 sm:pt-3 flex justify-between items-center">
                  <span className="text-base sm:text-lg font-bold">Total</span>
                  <span className="text-xl sm:text-2xl font-bold text-yellow-600">₹{total.toLocaleString()}</span>
                </div>
              </div>

              <div className="bg-green-50 border border-green-200 rounded-lg p-3 sm:p-4 text-xs sm:text-sm">
                <p className="font-semibold text-green-800 mb-1 sm:mb-2">✓ 100% Secure Payment</p>
                <p className="text-green-700">Protected by Razorpay&apos;s secure payment gateway</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Payment;
